import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Android as AndroidIcon,
  Apple as AppleIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon,
  OpenInNew as OpenIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import AppService from "../services/AppService";
import SmartLinkService from "../services/SmartLinkService";
import { useAuth } from "../hooks/useAuth";
import type { App } from "../types/app";
import type { SmartLink } from "../types/smartLink";

const PUBLIC_APP_URL = (process.env.REACT_APP_PUBLIC_APP_URL || window.location.origin).replace(
  /\/$/,
  "",
);

const SmartLinks: React.FC = () => {
  const { user } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [links, setLinks] = useState<SmartLink[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    Promise.all([
      // Registered apps use the account email as their legacy owner key.
      // Smart-link ownership itself uses the stable Firebase UID.
      AppService.getUserApps(user.email || user.uid),
      SmartLinkService.getUserLinks(user.uid),
    ])
      .then(([loadedApps, loadedLinks]) => {
        if (!active) return;
        const sortedApps = [...loadedApps].sort(
          (a, b) => Number(Boolean(b.favorite)) - Number(Boolean(a.favorite)) || a.name.localeCompare(b.name),
        );
        setApps(sortedApps);
        setLinks(loadedLinks);
        const firstEligible = sortedApps.find(
          (app) => app.androidStoreUrl || app.iosStoreUrl || app.storeUrl,
        );
        setSelectedAppId(firstEligible?.appId || sortedApps[0]?.appId || "");
      })
      .catch(() => {
        if (active) setMessage({ type: "error", text: "Could not load your apps and smart links." });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const selectedApp = useMemo(
    () => apps.find((app) => app.appId === selectedAppId) || null,
    [apps, selectedAppId],
  );
  const selectedLink = useMemo(
    () => links.find((link) => link.appId === selectedAppId) || null,
    [links, selectedAppId],
  );
  const publicUrl = selectedLink ? `${PUBLIC_APP_URL}/go/${selectedLink.slug}` : "";

  const handleSave = async () => {
    if (!user || !selectedApp) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await SmartLinkService.saveLink(
        selectedApp,
        user.uid,
        selectedLink?.slug,
      );
      setLinks((current) => [saved, ...current.filter((link) => link.appId !== saved.appId)]);
      setMessage({
        type: "success",
        text: selectedLink ? "Smart link updated with the latest store URLs." : "Smart link created and ready to share.",
      });
    } catch (error: any) {
      setMessage({ type: "error", text: error?.message || "Could not save this smart link." });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (value: string, label = "Smart link") => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage({ type: "success", text: `${label} copied to your clipboard.` });
    } catch {
      setMessage({ type: "error", text: "Copy failed. Select the link and copy it manually." });
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1120, mx: "auto", py: { xs: 2, md: 4 } }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Chip icon={<LinkIcon />} label="One link · Both stores" color="primary" sx={{ alignSelf: "flex-start" }} />
        <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1.5 }}>
          App smart links
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
          Share one URL everywhere. Mobile visitors go to the right store automatically, while desktop visitors can choose.
        </Typography>
      </Stack>

      {message && <Alert severity={message.type} sx={{ mb: 3 }}>{message.text}</Alert>}

      {apps.length === 0 ? (
        <Alert severity="info">Register an app and add at least one store URL before creating a smart link.</Alert>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card variant="outlined" sx={{ borderRadius: 4 }}>
              <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                <Typography variant="h5" sx={{ fontWeight: 750, mb: 0.75 }}>Create your link</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  The link stays the same when you update its store destinations.
                </Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="smart-link-app-label">App</InputLabel>
                  <Select
                    labelId="smart-link-app-label"
                    label="App"
                    value={selectedAppId}
                    onChange={(event) => {
                      setSelectedAppId(event.target.value);
                      setMessage(null);
                    }}
                  >
                    {apps.map((app) => <MenuItem key={app.appId} value={app.appId}>{app.name}</MenuItem>)}
                  </Select>
                </FormControl>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
                  <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <AndroidIcon color="success" /><Typography fontWeight={700}>Google Play</Typography>
                      </Stack>
                      <Typography variant="body2" color={selectedApp?.androidStoreUrl || selectedApp?.platform === "android" ? "text.primary" : "text.secondary"} sx={{ wordBreak: "break-all" }}>
                        {selectedApp?.androidStoreUrl || (selectedApp?.platform === "android" ? selectedApp.storeUrl : "") || "Not configured"}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <AppleIcon /><Typography fontWeight={700}>App Store</Typography>
                      </Stack>
                      <Typography variant="body2" color={selectedApp?.iosStoreUrl || selectedApp?.platform === "ios" ? "text.primary" : "text.secondary"} sx={{ wordBreak: "break-all" }}>
                        {selectedApp?.iosStoreUrl || (selectedApp?.platform === "ios" ? selectedApp.storeUrl : "") || "Not configured"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>

                {selectedLink && (
                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Your smart link"
                      value={publicUrl}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy smart link">
                              <IconButton onClick={() => handleCopy(publicUrl, "Smart link")} edge="end">
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Live app domain"
                      value={PUBLIC_APP_URL}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Copy live app domain">
                              <IconButton onClick={() => handleCopy(PUBLIC_APP_URL, "Live app domain")}>
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open live app domain">
                              <IconButton href={PUBLIC_APP_URL} target="_blank" rel="noopener noreferrer" edge="end">
                                <OpenIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {selectedLink.androidStoreUrl && (
                      <TextField
                        fullWidth
                        label="Google Play live app link"
                        value={selectedLink.androidStoreUrl}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start"><AndroidIcon color="success" /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copy Google Play link">
                                <IconButton onClick={() => handleCopy(selectedLink.androidStoreUrl, "Google Play link")}>
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open live app">
                                <IconButton href={selectedLink.androidStoreUrl} target="_blank" rel="noopener noreferrer" edge="end">
                                  <OpenIcon />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    {selectedLink.iosStoreUrl && (
                      <TextField
                        fullWidth
                        label="App Store live app link"
                        value={selectedLink.iosStoreUrl}
                        InputProps={{
                          readOnly: true,
                          startAdornment: <InputAdornment position="start"><AppleIcon /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copy App Store link">
                                <IconButton onClick={() => handleCopy(selectedLink.iosStoreUrl, "App Store link")}>
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open live app">
                                <IconButton href={selectedLink.iosStoreUrl} target="_blank" rel="noopener noreferrer" edge="end">
                                  <OpenIcon />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Stack>
                )}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSave}
                    disabled={saving || !selectedApp}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : selectedLink ? <SyncIcon /> : <LinkIcon />}
                  >
                    {selectedLink ? "Update destinations" : "Generate smart link"}
                  </Button>
                  {selectedLink && (
                    <>
                      <Button variant="outlined" size="large" onClick={() => handleCopy(publicUrl, "Smart link")} startIcon={<CopyIcon />}>Copy</Button>
                      <Button variant="text" size="large" href={publicUrl} target="_blank" rel="noopener noreferrer" startIcon={<OpenIcon />}>Preview</Button>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 4, color: "white", background: "linear-gradient(145deg, #111827 0%, #312e81 100%)" }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="overline" sx={{ opacity: 0.72 }}>How it routes</Typography>
                <Typography variant="h5" sx={{ fontWeight: 750, mb: 3 }}>One campaign URL, no guesswork</Typography>
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={2}><AndroidIcon /><Box><Typography fontWeight={700}>Android</Typography><Typography variant="body2" sx={{ opacity: 0.72 }}>Opens Google Play.</Typography></Box></Stack>
                  <Stack direction="row" spacing={2}><AppleIcon /><Box><Typography fontWeight={700}>iPhone & iPad</Typography><Typography variant="body2" sx={{ opacity: 0.72 }}>Opens the App Store.</Typography></Box></Stack>
                  <Stack direction="row" spacing={2}><LinkIcon /><Box><Typography fontWeight={700}>Desktop</Typography><Typography variant="body2" sx={{ opacity: 0.72 }}>Shows a clean store chooser.</Typography></Box></Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default SmartLinks;
